/**
 * Mali belgeler: serbest meslek makbuzu ya da fatura.
 *
 * Hangisi olduğuna merkez karar veriyor (Paketler → Belge ayarları).
 * Serbest meslek erbabı psikolog makbuz keser; Ltd./A.Ş. çatısı
 * altındaki merkez fatura.
 *
 * ORANLARI SİSTEM UYDURMUYOR. KDV ve stopaj oranı merkezin mali
 * müşavirinin kararı; ekranda varsayılan olarak merkezin kendi
 * ayarladığı oran geliyor ve belge başına değiştirilebiliyor.
 *
 * Entegratör henüz bağlı değil: belge dışarıda kesiliyor, numarası
 * buraya yazılıyor. Küçük muayenehanelerin zaten yaptığı şey.
 */

import { useCallback, useEffect, useState } from 'react'
import {
  ApiHatasi,
  BELGE_ADI,
  BELGE_DURUMU,
  api,
  paraYaz,
  type BelgeAyarlari,
  type BelgeHesabi,
  type BelgeOzet,
} from '../api'

function gun(s: string | null): string {
  if (!s) return '—'
  const d = new Date(s)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('tr-TR', { dateStyle: 'medium' })
}

export function Belgeler({
  danisanId,
  danisanAdi,
  yazabilir,
  onGeri,
}: {
  danisanId: string
  danisanAdi: string
  yazabilir: boolean
  onGeri: () => void
}) {
  const [liste, setListe] = useState<BelgeOzet[] | null>(null)
  const [ayarlar, setAyarlar] = useState<BelgeAyarlari | null>(null)
  /**
   * Alıcı adı danışan dosyasından geliyor ama VERGİ NUMARASI oradan
   * gelmiyor: kimlik numarası klinik dosyanın parçası değil, yalnızca
   * mali kaydın. Uygulayıcı her belgede elle giriyor.
   */
  const [ad, setAd] = useState(danisanAdi)
  const [formAcik, setFormAcik] = useState(false)
  const [kesilen, setKesilen] = useState<{ id: string; no: string } | null>(null)
  const [hata, setHata] = useState<string | null>(null)

  const yukle = useCallback(async () => {
    setHata(null)
    try {
      const [b, a] = await Promise.all([api.belgeler(danisanId), api.belgeAyarlari()])
      setListe(b.belgeler)
      setAyarlar(a.ayarlar)
    } catch {
      setHata('Belgeler alınamadı.')
      setListe([])
    }
  }, [danisanId])

  useEffect(() => {
    void yukle()
  }, [yukle])

  useEffect(() => {
    if (ad) return
    api
      .danisan(danisanId)
      .then((d) => {
        const k = d.danisan as Record<string, string | null>
        setAd(`${k['firstName'] ?? ''} ${k['lastName'] ?? ''}`.trim())
      })
      .catch(() => undefined)
  }, [ad, danisanId])

  /*
   * HATA EKRANA ÇIKIYOR.
   *
   * Yükleme düşünce `setHata` yazılıyordu ama `ayarlar` null kaldığı
   * için bu erken dönüş hata JSX'inden ÖNCE geliyor ve ekran sonsuza
   * kadar "Yükleniyor…" gösteriyordu. Uygulayıcı makbuz ekranını hiç
   * açamıyor, sebebini de göremiyordu.
   */
  if (hata && (liste === null || !ayarlar)) {
    return (
      <>
        <div className="uyari">{hata}</div>
        <div className="dugmeler">
          <button onClick={() => void yukle()}>Tekrar dene</button>
        </div>
      </>
    )
  }
  if (liste === null || !ayarlar) return <p className="alt">Yükleniyor…</p>

  const kesildi = async () => {
    if (!kesilen || !kesilen.no.trim()) return
    try {
      await api.belgeKesildi(kesilen.id, kesilen.no.trim())
      setKesilen(null)
      await yukle()
    } catch (e) {
      setHata(
        e instanceof ApiHatasi && e.kod === 'zaten_kesilmis'
          ? 'Bu belge zaten kesilmiş.'
          : 'İşaretlenemedi.',
      )
    }
  }

  const iptal = async (b: BelgeOzet) => {
    try {
      await api.belgeIptal(b.id)
      await yukle()
    } catch {
      setHata('İptal edilemedi.')
    }
  }

  return (
    <>
      <div className="dugmeler" style={{ marginBottom: 12 }}>
        <button onClick={onGeri}>← Paket ve ödeme</button>
      </div>

      <h1>{BELGE_ADI[ayarlar.belgeTuru]}</h1>
      <p className="alt">
        {ad} · merkez ayarı: {BELGE_ADI[ayarlar.belgeTuru]}
      </p>

      {hata && <div className="uyari">{hata}</div>}

      {!ayarlar.unvan && (
        <div className="bilgi">
          Merkezin unvanı ve vergi bilgisi girilmemiş. Belge kesmeden önce Paketler
          ekranındaki <strong>Belge ayarları</strong> bölümünü doldurun.
        </div>
      )}

      {yazabilir && (
        <div className="dugmeler" style={{ marginBottom: 14 }}>
          <button className="birincil" onClick={() => setFormAcik((v) => !v)}>
            {formAcik ? 'Vazgeç' : 'Belge oluştur'}
          </button>
        </div>
      )}

      {formAcik && (
        <BelgeFormu
          danisanId={danisanId}
          danisanAdi={ad}
          ayarlar={ayarlar}
          onIptal={() => setFormAcik(false)}
          onKayit={() => {
            setFormAcik(false)
            void yukle()
          }}
        />
      )}

      {liste.length === 0 ? (
        <div className="bilgi">Bu dosyada belge yok.</div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Belge no</th>
              <th>Alıcı</th>
              <th>Brüt</th>
              <th>KDV</th>
              <th>Stopaj</th>
              <th>Tahsilat</th>
              <th>Durum</th>
              {yazabilir && <th />}
            </tr>
          </thead>
          <tbody>
            {liste.map((b) => (
              <tr key={b.id} style={{ opacity: b.durum === 'cancelled' ? 0.5 : 1 }}>
                <td>
                  {b.belgeNo ?? '—'}
                  <div className="alt" style={{ margin: 0, fontSize: 12 }}>
                    {b.kesildi ? gun(b.kesildi) : gun(b.olusturuldu)}
                  </div>
                </td>
                <td>{b.alici}</td>
                <td>{b.brut ? paraYaz(b.brut, b.paraBirimi) : '—'}</td>
                <td>{b.kdv ? paraYaz(b.kdv, b.paraBirimi) : '—'}</td>
                <td>{b.stopaj && Number(b.stopaj) > 0 ? paraYaz(b.stopaj, b.paraBirimi) : '—'}</td>
                <td>
                  <strong>{paraYaz(b.tahsilat, b.paraBirimi)}</strong>
                </td>
                <td>
                  <span
                    className={
                      'rozet' +
                      (b.durum === 'issued' ? ' ok' : b.durum === 'cancelled' ? ' bad' : ' warn')
                    }
                  >
                    {BELGE_DURUMU[b.durum] ?? b.durum}
                  </span>
                </td>
                {yazabilir && (
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {b.durum === 'pending' && (
                      <button onClick={() => setKesilen({ id: b.id, no: '' })}>
                        Kesildi işaretle
                      </button>
                    )}
                    {b.durum !== 'cancelled' && (
                      <button style={{ marginLeft: 6 }} onClick={() => void iptal(b)}>
                        İptal
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {kesilen && (
        <div className="kart" style={{ marginTop: 14 }}>
          <label>
            Belge numarası
            <input
              value={kesilen.no}
              onChange={(e) => setKesilen({ ...kesilen, no: e.target.value })}
              placeholder="Muhasebe programındaki numara"
            />
          </label>
          <p className="alt" style={{ margin: '0 0 10px' }}>
            Numara zorunlu: numarasız "kesildi" işareti mali kaydı doğrulanamaz hale getirir.
          </p>
          <div className="dugmeler">
            <button className="birincil" disabled={!kesilen.no.trim()} onClick={() => void kesildi()}>
              İşaretle
            </button>
            <button onClick={() => setKesilen(null)}>Vazgeç</button>
          </div>
        </div>
      )}

      <p className="alt" style={{ marginTop: 16 }}>
        Entegratör henüz bağlı değil; belge muhasebe programınızda kesiliyor, numarası buraya
        yazılıyor. Kesilmiş belgenin tutarı ve alıcısı değiştirilemez — yanlışsa iptal edip
        yenisini kesin.
      </p>
    </>
  )
}

/* ------------------------------------------------------------------ */

function BelgeFormu({
  danisanId,
  danisanAdi,
  ayarlar,
  onIptal,
  onKayit,
}: {
  danisanId: string
  danisanAdi: string
  ayarlar: BelgeAyarlari
  onIptal: () => void
  onKayit: () => void
}) {
  const [aliciAdi, setAliciAdi] = useState(danisanAdi)
  const [vergiNo, setVergiNo] = useState('')
  const [adres, setAdres] = useState('')
  const [giris, setGiris] = useState<'brut' | 'tahsilat'>('tahsilat')
  const [tutar, setTutar] = useState('')
  const [kdv, setKdv] = useState(Number(ayarlar.kdvOrani ?? 0))
  const [stopaj, setStopaj] = useState(Number(ayarlar.stopajOrani ?? 0))
  const [hesap, setHesap] = useState<BelgeHesabi | null>(null)
  const [fark, setFark] = useState(0)
  const [hata, setHata] = useState<string | null>(null)
  const [bekliyor, setBekliyor] = useState(false)

  const onizle = useCallback(async () => {
    const n = Number(tutar.replace(',', '.'))
    if (!n || Number.isNaN(n)) {
      setHesap(null)
      return
    }
    try {
      const r = await api.belgeHesap({
        tur: ayarlar.belgeTuru,
        ...(giris === 'brut' ? { brut: n } : { tahsilat: n }),
        kdvOrani: kdv,
        stopajOrani: stopaj,
      })
      setHesap(r.hesap)
      setFark(r.yuvarlamaFarki)
    } catch {
      setHesap(null)
    }
  }, [tutar, giris, kdv, stopaj, ayarlar.belgeTuru])

  useEffect(() => {
    void onizle()
  }, [onizle])

  const gonder = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!hesap) return
    setHata(null)
    setBekliyor(true)
    try {
      await api.belgeOlustur(danisanId, {
        aliciAdi: aliciAdi.trim(),
        aliciVergiNo: vergiNo.trim() || null,
        aliciAdres: adres.trim() || null,
        brut: hesap.brut.toFixed(2),
        kdvOrani: kdv,
        stopajOrani: stopaj,
        paraBirimi: 'TRY',
      })
      onKayit()
    } catch {
      setHata('Belge oluşturulamadı.')
    } finally {
      setBekliyor(false)
    }
  }

  const smm = ayarlar.belgeTuru === 'smm'

  return (
    <form className="kart" onSubmit={gonder}>
      <h2 style={{ marginTop: 0 }}>
        Yeni {(BELGE_ADI[ayarlar.belgeTuru] ?? 'belge').toLocaleLowerCase('tr-TR')}
      </h2>
      {hata && <div className="uyari">{hata}</div>}

      <div className="satir">
        <label>
          Alıcı adı / unvanı
          <input value={aliciAdi} onChange={(e) => setAliciAdi(e.target.value)} required />
        </label>
        <label>
          TCKN / VKN
          <input value={vergiNo} onChange={(e) => setVergiNo(e.target.value)} maxLength={20} />
        </label>
        <label>
          Adres
          <input value={adres} onChange={(e) => setAdres(e.target.value)} maxLength={400} />
        </label>
      </div>

      <div className="satir">
        <label>
          Tutar girişi
          <select value={giris} onChange={(e) => setGiris(e.target.value as never)}>
            <option value="tahsilat">Tahsil edilen tutardan</option>
            <option value="brut">Brüt ücretten</option>
          </select>
        </label>
        <label>
          {giris === 'brut' ? 'Brüt ücret' : 'Tahsil edilen'}
          <input value={tutar} onChange={(e) => setTutar(e.target.value)} required />
        </label>
        <label>
          KDV oranı (%)
          <input
            type="number"
            min={0}
            max={100}
            step={0.5}
            value={kdv}
            onChange={(e) => setKdv(Number(e.target.value))}
          />
        </label>
        {smm && (
          <label>
            Stopaj oranı (%)
            <input
              type="number"
              min={0}
              max={100}
              step={0.5}
              value={stopaj}
              onChange={(e) => setStopaj(Number(e.target.value))}
            />
          </label>
        )}
      </div>

      {smm && stopaj > 0 && (
        <div className="bilgi">
          Stopaj yalnızca ödemeyi yapan taraf kurum ya da vergi mükellefi işletme olduğunda
          kesiliyor. Bireysel danışanda sıfır olmalı.
        </div>
      )}

      {hesap && (
        <div className="alan" style={{ marginTop: 10 }}>
          <div className="alan-olcut" style={{ marginTop: 0 }}>
            <span>
              Brüt: <strong style={{ color: 'var(--ink)' }}>{paraYaz(hesap.brut, 'TRY')}</strong>
            </span>
            <span>
              KDV (%{hesap.kdvOrani}):{' '}
              <strong style={{ color: 'var(--ink)' }}>{paraYaz(hesap.kdv, 'TRY')}</strong>
            </span>
            {smm && (
              <>
                <span>
                  Stopaj (%{hesap.stopajOrani}):{' '}
                  <strong style={{ color: 'var(--ink)' }}>{paraYaz(hesap.stopaj, 'TRY')}</strong>
                </span>
                <span>
                  Net ücret:{' '}
                  <strong style={{ color: 'var(--ink)' }}>{paraYaz(hesap.net, 'TRY')}</strong>
                </span>
              </>
            )}
            <span>
              Tahsilat:{' '}
              <strong style={{ color: 'var(--accent)' }}>{paraYaz(hesap.tahsilat, 'TRY')}</strong>
            </span>
          </div>
          {fark !== 0 && (
            <div className="alan-kaynak">
              Yuvarlama farkı {paraYaz(fark, 'TRY')}. Brütü elle yuvarlamak isteyebilirsiniz.
            </div>
          )}
        </div>
      )}

      <div className="dugmeler">
        <button className="birincil" type="submit" disabled={bekliyor || !hesap}>
          {bekliyor ? 'Kaydediliyor…' : 'Taslağı oluştur'}
        </button>
        <button type="button" onClick={onIptal}>
          Vazgeç
        </button>
      </div>
      <p className="alt" style={{ margin: '10px 0 0' }}>
        Oranlar merkezinizin ayarından geliyor ve mali müşavirinizin kararıdır; sistem oran
        önermiyor.
      </p>
    </form>
  )
}
